import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { GET_USER_HISTORY_QUERY_KEY } from '../../user-history/hooks/useGetUserHistory';
import { GET_ADMIN_WALLET_BALANCE_QUERY_KEY } from './useGetAdminWalletBalance';
import { GET_WALLET_TRANSACTIONS_QUERY_KEY } from './useGetWalletTransactions';

type AdminAdjustWalletInput = {
  userId: string;
  amount: number;
  reason: string;
};

type AdminAdjustWalletResult = {
  transactionId: string;
  previousBalance: number;
  newBalance: number;
  amount: number;
};

export const useAdminAdjustWallet = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['adminAdjustWallet'],
    mutationFn: async (input: AdminAdjustWalletInput) => {
      return client.fetchApi<AdminAdjustWalletResult, AdminAdjustWalletInput>({
        endpoint: 'wallet/admin/adjust',
        method: 'POST',
        body: input,
      });
    },
    onSuccess: (_data, variables) => {
      // WalletTransactionTable no longer renders on UserPage (epic
      // docs/epics/unified-player-history.md D-PLACE/M8, ledger
      // T8@eng-review) — GET_WALLET_TRANSACTIONS_QUERY_KEY's invalidation
      // below is now a harmless no-op kept for the still-existing hook/test,
      // but UserHistorySection's admin feed is the one an admin actually
      // sees post-adjustment, and it's a DIFFERENT top-level query key
      // ('admin-user-history' vs 'wallet') that the pre-existing
      // invalidation never covered — without this, a fresh ADMIN_ADJUSTMENT
      // row would silently not appear until the 30s staleTime lapsed.
      queryClient.invalidateQueries({
        queryKey: [GET_USER_HISTORY_QUERY_KEY, variables.userId],
      });
      queryClient.invalidateQueries({
        queryKey: GET_WALLET_TRANSACTIONS_QUERY_KEY(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: GET_ADMIN_WALLET_BALANCE_QUERY_KEY(variables.userId),
      });
    },
  });
};
